import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { SlidersHorizontal, Save, Loader2 } from "lucide-react";

// Project-level HMS tonnage assumptions editor (average thickness + bulk density). These feed
// every corridor tonnage, the geomorphology chapter and the Portfolio HMS Ranking.
export default function HMSAssumptionsCard({ projectId, onSaved }) {
  const [a, setA] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/hms-assumptions`);
        if (!cancelled) setA(data);
      } catch (e) {
        if (!cancelled) toast.error(apiError(e));
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const set = (k, v) => setA((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/projects/${projectId}/hms-assumptions`, {
        thickness_low_m: Number(a.thickness_low_m),
        thickness_high_m: Number(a.thickness_high_m),
        bulk_density: Number(a.bulk_density),
      });
      setA({ thickness_low_m: data.thickness_low_m, thickness_high_m: data.thickness_high_m, bulk_density: data.bulk_density });
      toast.success("Tonnage assumptions saved - reports & ranking will use these");
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!a) return null;

  const field = (label, k, step, unit) => (
    <label className="flex-1 min-w-[120px]">
      <span className="block text-[11px] text-slate-300 mb-1">{label}</span>
      <div className="flex items-center gap-1 bg-white/10 rounded-md px-2 h-9">
        <input data-testid={`hms-assump-${k}`} type="number" step={step} value={a[k]}
          onChange={(e) => set(k, e.target.value)}
          className="w-full bg-transparent text-white text-sm outline-none tabular-nums" />
        <span className="text-[11px] text-slate-400 shrink-0">{unit}</span>
      </div>
    </label>
  );

  return (
    <div className="rounded-xl p-4" style={{ background: "#0B1420" }} data-testid="hms-assumptions-card">
      <div className="flex items-center gap-2 mb-2">
        <SlidersHorizontal size={15} style={{ color: "#C20511" }} />
        <span className="text-sm font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>HMS Tonnage Assumptions</span>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">Set the average mineralised thickness and bulk density used for indicative heavy-mineral tonnages across this project's reports and ranking.</p>
      <div className="flex flex-wrap gap-3 items-end">
        {field("Min thickness", "thickness_low_m", "0.5", "m")}
        {field("Max thickness", "thickness_high_m", "0.5", "m")}
        {field("Bulk density", "bulk_density", "0.1", "t/m³")}
        <button data-testid="hms-assump-save" onClick={save} disabled={saving}
          className="h-9 px-4 rounded-md text-xs font-semibold flex items-center gap-1.5 disabled:opacity-50" style={{ background: "#C20511", color: "#FFFFFF" }}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
        </button>
      </div>
    </div>
  );
}
